import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useMilestone, usePopupOutsideClose } from '../../lib/hooks';

import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import { alertMessage } from '../../lib/actions';
import classNames from 'classnames';
import Icon from '../ds/Icon';

const dt = new Date();
dt.setMonth(dt.getMonth() - 1);

export const MilestoneSwitch = () => {

    const [showList, setShowList] = useState(true);
    const { milestoneList, modalStatus } = useSelector(st => ({
        milestoneList: st.milestoneList,
        modalStatus: st.modalStatus
    }));
    const [value, setValue] = useState([dt, new Date()]);
    const [, setSearchParams] = useSearchParams();

    const milestone = useMilestone();
    useEffect(() => {
        if (!milestone) {
            return;
        }
        setValue([
            moment(milestone.start).toDate(),
            moment(milestone.end).toDate(),
        ]);
    }, [milestone]);

    const dispatch = useDispatch();
    const isOpen = modalStatus.popupName === 'milestone-switch';
    const ref = useRef(null);
    usePopupOutsideClose(ref, isOpen);

    const toggleFilter = () => {
        dispatch({ type: 'popup', name: isOpen ? '' : 'milestone-switch' });
    };

    const onChange = (value) => {
        if (!value) {
            const nsp = new URLSearchParams(document.location.search);
            nsp.delete('start');
            nsp.delete('end');
            setSearchParams(nsp);
            return;
        }
        const start = moment(value[0]);
        const end = moment(value[1]);
        if (end.diff(start, 'days') > 365) {
            dispatch(alertMessage('You can check stats for a maximum duration of 365 days', 'Error', 'ds-tone-bad'));
            return;
        }
        const nsp = new URLSearchParams(document.location.search);
        nsp.set('start', start.format('YYYY-MM-DD'));
        nsp.set('end', end.format('YYYY-MM-DD'));
        setSearchParams(nsp);
    };

    const np = new URLSearchParams(document.location.search);

    // The currently selected range — URL params take precedence, else the
    // date-range picker value. Any sprint whose range overlaps this gets a dot.
    const selStart = np.get('start') ? moment(np.get('start')) : (value?.[0] ? moment(value[0]) : null);
    const selEnd = np.get('end') ? moment(np.get('end')) : (value?.[1] ? moment(value[1]) : null);
    const overlapsSelection = (item) => {
        const s = moment(item.start || item.start_date);
        const e = moment(item.end || item.due_date);
        return selStart && selEnd && s.isValid() && e.isValid()
            && s.isSameOrBefore(selEnd, 'day') && selStart.isSameOrBefore(e, 'day');
    };

    return (
        <div className="ds-dropdown" ref={ref}>
            <button
                type="button"
                className="ds-btn is-soft ds-trigger"
                onClick={toggleFilter}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <span>{milestone?.title || 'Select period'}</span>
                <Icon name="chevronDown" className="ds-trigger__caret" />
            </button>

            <div className={classNames("ds-dropdown__panel", { "is-open": isOpen })} style={{ width: '288px' }}>
                <div className="ds-mile-picker">
                    <DateRangePicker
                        onCalendarClose={() => setShowList(true)}
                        onCalendarOpen={() => setShowList(false)}
                        onChange={onChange}
                        value={value}
                        maxDate={moment().add(15, 'day').toDate()}
                    />
                </div>

                {showList && milestoneList?.map(item => {
                    np.set('start', item.start);
                    np.set('end', item.end);
                    const inRange = overlapsSelection(item);
                    return (
                        <Link
                            key={item.id}
                            to={`${document.location.pathname}?${np.toString()}`}
                            className={classNames("ds-dropdown__item", { "is-muted": item.state !== 'active' })}
                        >
                            <span className="ds-mile-label">
                                <span
                                    className={classNames("ds-mile-dot", { "is-on": inRange })}
                                    title={inRange ? 'Overlaps the selected period' : undefined}
                                    aria-hidden="true"
                                />
                                <span className="ds-mile-title">{item.title}</span>
                            </span>
                            <span className="ds-badge" title={`${item.start_date} - ${item.due_date}`}>
                                {moment(item.start_date).format("DD")}–{moment(item.due_date).format("DD")}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};
